import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import posthog from 'posthog-js';
import useAuthStore from './store/useAuthStore';
import useThemeStore from './store/useThemeStore';
import useCurrencyStore from './store/useCurrencyStore';
import useInboxStore from './store/useInboxStore';
import NotificationListener from './components/NotificationListener';

// Initialize PostHog
posthog.init('phc_chmE4whvg1Pm76NCGWGtvHyZFeqLRnYNkojlkIrWeKA', {
  api_host: 'https://us.i.posthog.com',
  capture_pageview: false, // we capture manually on route change
  capture_pageleave: true,
  autocapture: true,
});

import WaitlistPage from './pages/WaitlistPage';
import SplashPage from './pages/SplashPage';
import OnboardingPage from './pages/OnboardingPage';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import ExplorePage from './pages/ExplorePage';
import ProviderProfilePage from './pages/ProviderProfilePage';
import ServiceListingPage from './pages/ServiceListingPage';
import BookingsPage from './pages/BookingsPage';
import InboxPage from './pages/InboxPage';
import AccountPage from './pages/AccountPage';
import NotificationsPage from './pages/NotificationsPage';
import WalletPage from './pages/WalletPage';
import VerificationPage from './pages/VerificationPage';
import CreateServicePage from './pages/CreateServicePage';
import MyServicesPage from './pages/MyServicesPage';
import FavoritesPage from './pages/FavoritesPage';
import AdminPage from './pages/AdminPage';
import EarningsPage from './pages/EarningsPage';
import ProviderSetupPage from './pages/ProviderSetupPage';
import ClientSetupPage from './pages/ClientSetupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import HelpPage from './pages/HelpPage';
import PrivacyPage from './pages/PrivacyPage';
import MyReviewsPage from './pages/MyReviewsPage';
import BottomNav from './components/BottomNav';
import ScrollToTop from './components/ScrollToTop';
import Toast from './components/Toast';

// Nav page order for determining swipe direction
const NAV_ORDER = ['/home', '/inbox', '/explore', '/bookings', '/account'];

function getNavIndex(path) {
  return NAV_ORDER.indexOf(path);
}

const pageVariants = {
  enter: (direction) => ({
    x: direction === 0 ? 0 : direction > 0 ? '25%' : '-25%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction === 0 ? 0 : direction > 0 ? '-25%' : '25%',
    opacity: 0,
  }),
};

const pageTransition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.22,
};

// Animated routes wrapper — swipe between nav pages
function AnimatedRoutes({ isAuthenticated }) {
  const location = useLocation();
  const prevIndexRef = useRef(getNavIndex(location.pathname));
  const currIndex = getNavIndex(location.pathname);
  const isNavPage = currIndex !== -1;
  const prevIsNav = prevIndexRef.current !== -1;

  let direction = 0;
  if (isNavPage && prevIsNav) {
    direction = currIndex > prevIndexRef.current ? 1 : currIndex < prevIndexRef.current ? -1 : 0;
  }

  useEffect(() => {
    if (isNavPage) {
      prevIndexRef.current = currIndex;
    }
  }, [location.pathname, currIndex, isNavPage]);

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={isNavPage ? location.pathname : 'other'}
        custom={direction}
        variants={isNavPage ? pageVariants : undefined}
        initial={isNavPage ? 'enter' : false}
        animate={isNavPage ? 'center' : undefined}
        exit={isNavPage ? 'exit' : undefined}
        transition={isNavPage ? pageTransition : { duration: 0 }}
        style={{ width: '100%', minHeight: '100vh' }}
      >
        <Routes location={location}>
          {/* Public routes */}
          <Route path="/" element={<SplashPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/auth" element={<AuthPage />} />

          {/* Protected routes */}
          <Route path="/home" element={<Protected><HomePage /></Protected>} />
          <Route path="/explore" element={<Protected><ExplorePage /></Protected>} />
          <Route path="/provider/:id" element={<Protected><ProviderProfilePage /></Protected>} />
          <Route path="/service/:id" element={<Protected><ServiceListingPage /></Protected>} />
          <Route path="/bookings" element={<Protected><BookingsPage /></Protected>} />
          <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
          <Route path="/account" element={<Protected><AccountPage /></Protected>} />
          <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
          <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
          <Route path="/verification" element={<Protected><VerificationPage /></Protected>} />
          <Route path="/create-service" element={<Protected><CreateServicePage /></Protected>} />
          <Route path="/edit-service/:id" element={<Protected><CreateServicePage /></Protected>} />
          <Route path="/my-services" element={<Protected><MyServicesPage /></Protected>} />
          <Route path="/favorites" element={<Protected><FavoritesPage /></Protected>} />
          <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
          <Route path="/earnings" element={<Protected><EarningsPage /></Protected>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/provider-setup" element={<Protected><ProviderSetupPage /></Protected>} />
          <Route path="/help" element={<Protected><HelpPage /></Protected>} />
          <Route path="/privacy" element={<Protected><PrivacyPage /></Protected>} />
          <Route path="/my-reviews" element={<Protected><MyReviewsPage /></Protected>} />
          <Route path="/client-setup" element={<Protected><ClientSetupPage /></Protected>} />

          <Route path="*" element={<Navigate to={isAuthenticated ? '/home' : '/'} replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

// Protected route wrapper
const Protected = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/auth" replace />;
};

// Track page views on route change
function PostHogPageView() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href });
  }, [location.pathname]);
  return null;
}

// Password gate — show waitlist unless access granted
const WAITLIST_MODE = true; // Set to false to disable waitlist gate

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const { theme, applyTheme } = useThemeStore();
  const { detectFromIP } = useCurrencyStore();
  const { chatOpen } = useInboxStore();
  const hasAccess = localStorage.getItem('findr_access') === 'granted';

  // Apply saved theme on initial load
  useEffect(() => { applyTheme(theme); }, [theme, applyTheme]);

  // Detect location currency on first load (only if not manually set)
  useEffect(() => { detectFromIP(); }, []); // eslint-disable-line

  // Identify user in PostHog when logged in
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.fullName,
        accountType: user.accountType,
      });
    } else {
      posthog.reset();
    }
  }, [isAuthenticated, user?.id]); // eslint-disable-line

  // If waitlist mode is on and user doesn't have access, show waitlist
  if (WAITLIST_MODE && !hasAccess && !isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<WaitlistPage />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <PostHogPageView />
      <ScrollToTop />
      <div className="min-h-screen bg-dark-bg font-inter overflow-x-hidden">
        <AnimatedRoutes isAuthenticated={isAuthenticated} />

        {/* Bottom nav — hidden when in active chat */}
        {isAuthenticated && !chatOpen && <BottomNav />}
        <Toast />
        <NotificationListener />
      </div>
    </Router>
  );
}

export default App;
